import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { PORTFOLIO, DEMO_DETAILS } from "../../data/content";
import { ChapterHeading } from "./ChapterHeading";
import { Reveal } from "./Reveal";
import { DemoPreview } from "./DemoPreview";
import { discussService } from "../../lib/scroll";

const BrowserMockup = ({ item }) => (
    <div className="rounded-xl border border-white/10 bg-[#0A0E17] overflow-hidden mb-5 transition-transform duration-500 group-hover:scale-[1.015]">
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/8 bg-[#0D131F]">
            <span className="h-2 w-2 rounded-full bg-white/15" />
            <span className="h-2 w-2 rounded-full bg-white/15" />
            <span className="h-2 w-2 rounded-full bg-white/15" />
            <span className="ml-2 h-3.5 flex-1 rounded bg-white/5" />
        </div>
        <div className="p-3 space-y-2">
            <div
                className={`h-20 rounded-lg relative overflow-hidden flex items-center justify-center ${
                    item.accent === "gold"
                        ? "bg-gradient-to-br from-[#1A1405] via-[#0D131F] to-[#131C2E]"
                        : "bg-gradient-to-br from-[#041A20] via-[#0D131F] to-[#131C2E]"
                }`}
            >
                <item.icon
                    size={26}
                    className={item.accent === "gold" ? "text-[#F5C518]/80" : "text-[#00E5FF]/80"}
                />
                <div
                    className={`absolute top-2 right-2 h-8 w-14 rounded blur-md ${
                        item.accent === "gold" ? "bg-[#F5C518]/20" : "bg-[#00E5FF]/20"
                    }`}
                />
            </div>
            <div className="h-2 w-3/4 rounded bg-white/10" />
            <div className="h-2 w-1/2 rounded bg-white/8" />
            <div className="flex gap-2 pt-1">
                <span
                    className={`h-4 w-14 rounded-full ${
                        item.accent === "gold" ? "bg-[#F5C518]/70" : "bg-[#00E5FF]/50"
                    }`}
                />
                <span className="h-4 w-12 rounded-full border border-white/15" />
            </div>
        </div>
    </div>
);

export const Portfolio = () => {
    const [active, setActive] = useState(null);

    return (
    <section id="work" data-testid="portfolio-section" className="py-24 lg:py-32">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
            <ChapterHeading
                label="Our Work"
                title="Solutions We Can Build"
                sub="Click any concept to open a sample preview of what we can build for that business type."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {PORTFOLIO.map((item, i) => (
                    <Reveal key={item.name} delay={(i % 4) * 0.07}>
                        <button
                            data-testid={`portfolio-card-${i}`}
                            onClick={() => setActive(item)}
                            className="group w-full text-left h-full rounded-2xl border border-white/8 bg-[#0D131F] p-4 transition-all duration-300 hover:border-[#00E5FF]/40 hover:-translate-y-1"
                        >
                            <BrowserMockup item={item} />
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <h3 className="font-display text-base font-semibold text-white leading-snug">
                                    {item.name}
                                </h3>
                                <span className="shrink-0 rounded-full border border-[#F5C518]/40 bg-[#F5C518]/10 px-2.5 py-0.5 font-jbmono text-[9px] uppercase tracking-[0.15em] text-[#F5C518]">
                                    Demo Concept
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 leading-relaxed">{item.desc}</p>
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {item.tags.map((t) => (
                                    <span
                                        key={t}
                                        className="rounded bg-white/5 px-2 py-0.5 text-[10px] text-slate-400"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </button>
                    </Reveal>
                ))}
            </div>
            <Reveal className="mt-8">
                <p className="text-xs text-slate-500 font-jbmono tracking-wide">
                    * All items shown are demo concepts / sample solutions — not completed client
                    projects.
                </p>
            </Reveal>
        </div>
        <AnimatePresence>
            {active && (
                <DemoPreview
                    name={active.name}
                    demo={DEMO_DETAILS[active.name]}
                    onClose={() => setActive(null)}
                    onBuild={discussService}
                />
            )}
        </AnimatePresence>
    </section>
    );
};
